import {SubscriptionDurationType} from "../../model/subscription";
import usePlanIntroduction, {PlanDetail} from "../../hook/usePlanIntroduction";
import React, {useCallback, useMemo, useState} from "react";
import {basicPlanId, proPlanId} from "../../core/variables";
import {useIntl} from "react-intl";
import classNames from "classnames";
import {Switch} from "@headlessui/react";
import useMemberInvitations from "../../query/invitation/useMemberInvitations";
import useUserMembers from "../../query/member/useUserMembers";
import useLoginUser from "../../query/auth/useLoginUser";
import checkIcon from "../../assets/svgs/check_icon.svg";
import ConfirmDialog, {getConfirmDialogId} from "../ConfirmDialog";
import useModal from "../../hook/useModal";
import useLinkHandler from "../../hook/useLinkHandler";

const CREATE_OR_GO_CHANNEL_DIALOG = "create-or-go-channel-dialog";

const HomeSubscription: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const { linkToOnboarding, linkToChannelList } = useLinkHandler();

    const planIds = [basicPlanId!, proPlanId!];
    const [period, setPeriod] = useState<SubscriptionDurationType>("month");

    const {data: profile} = useLoginUser();
    const {data: memberInvitations} = useMemberInvitations(profile);
    const {data: userMembers} = useUserMembers(profile);

    const {planDetails} = usePlanIntroduction();
    // const {data: planInfos, isLoading} = useSubscriptionPlansHome(intl.formatMessage({id: "i0062"}));

    const onSwitchPeriod = () => {
        setPeriod(period === "month" ? "year" : "month")
    }

    const onSelectPlan = useCallback((planId: string) => {
        if (memberInvitations?.length || userMembers?.length) {
            modal.open(getConfirmDialogId(CREATE_OR_GO_CHANNEL_DIALOG));
        } else {
            linkToOnboarding();
        }
    }, [memberInvitations?.length, modal, linkToOnboarding, userMembers?.length]);

    // if (isLoading) return <LoadingSpinner/>

    // if (!planInfos) return <div />

    return (
        <div className="w-[784px] h-fit flex flex-col items-center">
            <div className={"font-bold text-console-black text-[24px] text-center"}>
                {intl.formatMessage({id: "i0037"})}
            </div>
            <div className={"w-full flex justify-between h-[40px] mt-[40px]"}>
                <div className={"mt-[12px] leading-[28px] text-[14px] text-console-black font-medium"}>
                    {intl.formatMessage({id: "i0038"})}
                </div>
                <PeriodSwitch period={period} onSwitch={onSwitchPeriod} />
            </div>
            <div className="flex gap-x-[12px] mt-[14px]">
                {planIds.map((planId) => (
                    <PlanCard planId={planId}
                              planDetail={planDetails.filter(planDetail => planDetail._id === planId)[0]}
                              period={period}
                              onSelect={onSelectPlan}
                              key={planId}/>
                ))}
            </div>

            <ConfirmDialog dialogId={CREATE_OR_GO_CHANNEL_DIALOG}
                           content={intl.formatMessage({id: "i0057"})}
                           confirmVariant={"primary"}
                           onConfirm={() => linkToOnboarding()}
                           confirmText={intl.formatMessage({id: "i0058"})}
                           cancelText={intl.formatMessage({id: "i0059"})}
                           onCancel={() => linkToChannelList()}
                           showCancelButton={true}
                           closeTriggerOnCancel={false}
            />
        </div>
    )
}


interface PeriodSwitchProps {
    period: SubscriptionDurationType
    onSwitch: () => void
}


const PeriodSwitch: React.FC<PeriodSwitchProps> = ({period, onSwitch}) => {
    const intl = useIntl();
    const isMonth = useMemo(() => period === "month", [period])

    const switchStyle = useCallback((active: boolean) => {
        return classNames("font-semibold text-center text-[14px] tracking-[0.833px]",
            active ? "text-white" : "text-bunny-gray-700")
    }, [])

    const onClickToggle = () => {
        onSwitch();
    }

    const onClickMonthly = (e: React.MouseEvent) => {
        isMonth && e.preventDefault();
    }

    const onClickAnnually = (e: React.MouseEvent) => {
        !isMonth && e.preventDefault();
    }

    return (
        <Switch checked={isMonth}
                onChange={onClickToggle}
                className={classNames(
                    "relative flex items-center h-[40px] w-[220px] shrink-0 rounded-[6px] bg-white",
                    "border border-bunny-gray-300 transition-colors duration-200 ease-in-out focus:outline-none group"
                )}
        >
            <span className="sr-only"/>
            <span aria-hidden={true}
                  className={classNames(
                      isMonth ? "translate-x-0" : "translate-x-full",
                      "bg-bunny-default group-hover:bg-bunny-primary-700 group-active:bg-bunny-primary-900",
                      "inline-block h-[40px] w-[110px] transform rounded-[6px] ring-0 transition duration-200 ease-in-out"
                  )}
            />
            <div className="absolute w-full flex items-center">
                <div onClick={onClickMonthly}
                     className={classNames(switchStyle(isMonth), "w-1/2 h-full leading-[40px]")}>{intl.formatMessage({id: "i0039"})}</div>
                <div onClick={onClickAnnually}
                     className={classNames(switchStyle(!isMonth), "w-1/2 h-full leading-[40px]")}>{intl.formatMessage({id: "i0040"})}</div>
            </div>
        </Switch>
    )
}

type PlanCardProps = {
    planId: string
    period: SubscriptionDurationType
    planDetail: PlanDetail
    onSelect: (planId: string) => void
}
const PlanCard:React.FC<PlanCardProps> = (props) => {
    const {planId, period, planDetail, onSelect} = props;
    const intl = useIntl();

    const onClickChangeBtn = (e: React.MouseEvent) => {
        onSelect(planId)
    }

    const priceElement = useMemo(() => {
        switch (period) {
            case "month":
                return (
                    <div className="mt-[16px] border-b border-bunny-gray-300 pb-[32px] h-[70px] flex items-end">
                        <label className="text-[32px] font-bold text-console-black">
                            {intl.formatMessage({id: "i0041"}, {price: intl.formatNumber(planDetail.cost!.month)})}
                        </label>
                        <label className="text-[16px] font-semibold text-console-black pl-[1px]">
                            {intl.formatMessage({id: "i0041_1"})}
                        </label>
                    </div>
                )
            case "year":
                return (
                    <div className="mt-[16px] border-b border-bunny-gray-300 pb-[32px] h-[70px] flex items-end">
                        <label className="text-[32px] font-bold text-console-black">
                            {intl.formatMessage({id: "i0042"}, {price: intl.formatNumber(planDetail.cost!.year)})}
                        </label>
                        <label className="text-[16px] font-semibold text-console-black pl-[1px]">
                            {intl.formatMessage({id: "i0042_1"})}
                        </label>
                    </div>
                )
            default:
                return <></>
        }
    }, [intl, planDetail, period])

    return (
        <div className={"p-[32px] w-[386px] h-[620px] rounded-[6px] flex flex-col justify-between leading-none bg-white"}>
            <div className="text-bunny-default text-[24px] font-bold">{planDetail.name}</div>
            {priceElement}
            <div className="mt-[42px] flex flex-col space-y-[10px]">
                {(planDetail.info as any[])?.map((detail, index)=> (
                    <div key={`${planDetail.name}-${index}`}
                         className={classNames("h-[24px] flex items-center")}>
                        <img src={checkIcon} className={"mx-[5px]"} alt="check" width={14} height={14} />
                        <div className="px-[6px] text-[15px] font-semibold">
                            {detail.text}
                        </div>
                        <div className="text-[12px]">
                            {detail.sub}
                        </div>
                    </div>
                ))}
            </div>
            <div className={"cursor-pointer mt-auto w-full h-[64px] rounded-[6px] text-center leading-[64px] bg-bunny-default text-white text-[16px]"}
                 onClick={onClickChangeBtn}>
                {intl.formatMessage({id: "i0043"}, {name: planDetail.name})}
            </div>
        </div>
    )
}

export default HomeSubscription;
